import { StaticScreenProps, useNavigation } from "@react-navigation/native";
import { useCallback, useMemo } from "react";

import { Rater } from "../components";
import {
  Button,
  Col,
  Form,
  Input,
  TextInput,
  TextLabel,
  TextTitle,
} from "../lib";
import {
  useBookingReviewCreate,
  useBookingReviewUpdate,
  useBookings,
  useServerForm,
} from "../services";
import { useInit } from "../services/epox/utils";

const initialValues = {
  text: "",
  score: 0,
};

type Props = StaticScreenProps<{
  id: number;
}>;

export function BookingReviewScreen({ route }: Props) {
  // Params
  const { id } = route.params;

  // Data
  const { data: bookings } = useBookings();
  const [createReview, isCreating, createError] = useBookingReviewCreate({
    id,
  });
  const [updateReview, isUpdating, updateError] = useBookingReviewUpdate({
    id,
  });

  // Vars
  const error = createError || updateError;
  const { values, setValue, setValues } = useServerForm(initialValues, error);
  const navigation = useNavigation();
  const booking = useMemo(() => {
    return (bookings ?? []).find((b) => b.id === id) ?? null;
  }, [bookings, id]);

  const isLoading = isCreating || isUpdating;

  // Handlers
  const handleChangeScore = (value: number) => {
    setValue("score", value);
  };

  const handleSubmit = useCallback(async () => {
    if (booking) {
      if (booking.review) {
        await updateReview(values);
      } else {
        await createReview(values);
      }

      navigation.goBack();
    }
  }, [booking, createReview, navigation, updateReview, values]);

  // Effects
  useInit(() => {
    if (booking?.review) {
      setValues({
        score: booking.review.score,
        text: booking.review.comments,
      });
    }
  });

  // Render
  return (
    <Form>
      <Col.C my="8">
        <TextTitle mb="4">
          How was your date with {booking?.customer_name}?
        </TextTitle>

        <Rater value={values.score} onChange={handleChangeScore} />
        <TextLabel mb="2">(Tap a Star to Rate)</TextLabel>
      </Col.C>

      {!!values.score && (
        <>
          <Input label="Message">
            <TextInput
              name="text"
              multiline
              value={values.text}
              placeholder="Add comments"
              numberOfLines={10}
              onChange={setValue}
              disabled={!values.score}
            />
          </Input>

          <Button
            loading={isLoading}
            disabled={!values.score || !values.text}
            onPress={handleSubmit}
          >
            {booking?.review ? "Update" : "Submit"}
          </Button>
        </>
      )}
    </Form>
  );
}
